import { useState } from "react";

import {
  useCancelIntegration,
  useIntegrationsList,
  type IntegrationOp,
} from "../api";
import { PageHeader } from "../components/PageHeader";
import { formatRelative } from "../utils/formatRelative";
import { MailboxReadLogSection } from "./MailboxReadLogSection";
import { useDocumentTitle } from "../utils/useDocumentTitle";
import styles from "./IntegrationsRoute.module.css";
import { formatDurationSeconds } from "../utils/formatDuration";

// Friendly operation label from (provider, kind). Unknown combinations fall
// back to a readable join so a new kind added BE-side still renders sanely.
const OP_LABELS: Record<string, string> = {
  // The operator's word is "sync". "Sweep" is our word for the per-mailbox
  // mechanism and means nothing on this page, which is why the raw fallback
  // rendered it as "gmail mailbox sweep".
  "gmail:mailbox_sweep": "Gmail sync",
  // A conversation's refresh: only the mail that arrived since the last sync.
  "gmail:mailbox_new_mail": "Gmail new mail for a conversation",
  // An agent asked for these threads (``ingest_threads``). Older rows carry the
  // kind from before it was salted per request, hence two keys.
  "gmail:mailbox_ingest": "Gmail ingest request from agent",
  "gmail:mailbox_ingest_request": "Gmail ingest request from agent",
  "drive:global_folder_sync": "Global Drive folder sync",
  "drive:folder_sync": "Drive sync",
  "drive:sync": "Drive sync",
  "slack:slack_sync": "Slack sync",
};
const opLabel = (op: IntegrationOp): string =>
  OP_LABELS[`${op.provider}:${op.kind}`] ??
  `${op.provider} ${op.kind.replace(/_/g, " ")}`;

const STATUS_LABEL: Record<IntegrationOp["status"], string> = {
  queued: "Queued",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
};

const time = (iso: string | null): string => (iso ? formatRelative(iso) : "-");

// Absolute wall-clock start time (operator-local), to the second. The raw ISO
// (UTC) goes on the cell's title so an op can be correlated against external
// logs - Sentry, CloudWatch, a Google API audit - which are all in UTC.
const formatStarted = (iso: string | null): string => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// The per-mailbox sync has no single project - it reads one inbox on behalf of
// every project that inbox serves - so its scope column carries a
// ``mailbox:<id>`` key rather than a project id. Rendering that raw put an
// internal identifier in a column of project names; the Google account column
// beside it already says WHICH inbox, so this only has to say whose work it is.
const MAILBOX_SCOPE_PREFIX = "mailbox:";
// Likewise the Slack sync reads every subscribed channel in the workspace at once.
const SLACK_SCOPE_PREFIX = "slack:";
const projectLabel = (op: IntegrationOp): string => {
  if (op.project_id.startsWith(MAILBOX_SCOPE_PREFIX)) {
    return "All projects on this inbox";
  }
  if (op.project_id.startsWith(SLACK_SCOPE_PREFIX)) {
    return "All projects with Slack channels";
  }
  return op.project_name ?? op.project_id;
};

const parseMs = (iso: string | null): number | null =>
  iso ? Date.parse(iso) : null;

// "-" when not applicable.
const formatDuration = (ms: number | null): string =>
  ms === null || Number.isNaN(ms) || ms < 0 ? "-" : formatDurationSeconds(ms / 1000);

// Time spent waiting in the queue before running (started - enqueued).
const queueMs = (op: IntegrationOp): number | null => {
  const enq = parseMs(op.enqueued_at);
  const start = parseMs(op.started_at);
  return enq !== null && start !== null ? start - enq : null;
};

// Time spent running: finished - started for a terminal op, or live elapsed
// (now - started) for one still running. Null until it starts.
const runtimeMs = (op: IntegrationOp, nowMs: number): number | null => {
  const start = parseMs(op.started_at);
  if (start === null) return null;
  return (parseMs(op.finished_at) ?? nowMs) - start;
};

const CancelButton = ({ op }: { op: IntegrationOp }) => {
  const cancel = useCancelIntegration();
  const [error, setError] = useState<string | null>(null);
  return (
    <div className={styles.cancelCell}>
      <button
        type="button"
        className={styles.cancelBtn}
        disabled={cancel.isLoading}
        onClick={() => {
          setError(null);
          cancel.mutate(op.id, {
            // A 409 here means the op started between this render and the click
            // (only queued ops cancel) - surface it instead of failing silently.
            onError: (e) =>
              setError(e instanceof Error ? e.message : String(e)),
          });
        }}
      >
        {cancel.isLoading ? "Cancelling…" : "Cancel"}
      </button>
      {error && <span className={styles.cancelError}>Could not cancel - it may have already started.</span>}
    </div>
  );
};

const OpsTable = ({
  ops,
  showCancel,
  showDetail,
}: {
  ops: IntegrationOp[];
  showCancel?: boolean;
  // Completed ops carry a detail summary + a finished time; running/queued don't.
  showDetail?: boolean;
}) => {
  // Single "now" per render so live runtimes are consistent across rows; the
  // list re-polls every 5s, so a running op's runtime ticks up each refresh.
  const nowMs = Date.now();
  return (
    // Scrolls inside its own box when the page is too narrow, rather than
    // pushing the page wider than the window.
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Operation</th>
            <th>Project</th>
            <th>Google account</th>
            {showDetail && <th>Detail</th>}
            <th>Enqueued</th>
            <th>Started</th>
            <th>Queued for</th>
            <th>Runtime</th>
            <th className={styles.numCol}>Calls</th>
            <th>Status</th>
            {showCancel && <th />}
          </tr>
        </thead>
        <tbody>
          {ops.map((op) => (
            <tr key={op.id}>
              <td className={styles.opCell}>{opLabel(op)}</td>
              <td>{projectLabel(op)}</td>
              <td className={styles.accountCell}>{op.account_email}</td>
              {showDetail && (
                <td className={styles.detailCell}>{op.detail ?? "-"}</td>
              )}
              <td className={styles.nowrap}>{time(op.enqueued_at)}</td>
              <td className={styles.nowrap} title={op.started_at ?? undefined}>
                {formatStarted(op.started_at)}
              </td>
              <td className={styles.nowrap}>{formatDuration(queueMs(op))}</td>
              <td className={styles.nowrap}>
                {formatDuration(runtimeMs(op, nowMs))}
              </td>
              <td className={styles.numCol}>{op.call_count}</td>
              <td>
                <span
                  className={`${styles.badge} ${styles[`badge_${op.status}`]}`}
                >
                  {STATUS_LABEL[op.status]}
                </span>
                {op.status === "failed" && op.error && (
                  <div className={styles.errorText} title={op.error}>
                    {op.error}
                  </div>
                )}
              </td>
              {showCancel && (
                <td>
                  <CancelButton op={op} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Section = ({
  title,
  lede,
  ops,
  showCancel,
  showDetail,
}: {
  title: string;
  lede: string;
  ops: IntegrationOp[];
  showCancel?: boolean;
  showDetail?: boolean;
}) => (
  <section className={styles.section}>
    <div className={styles.sectionHead}>
      <h2 className={styles.h2}>
        {title} <span className={styles.count}>{ops.length}</span>
      </h2>
      <p className={styles.lede}>{lede}</p>
    </div>
    {ops.length === 0 ? (
      <p className={styles.empty}>Nothing here.</p>
    ) : (
      <OpsTable ops={ops} showCancel={showCancel} showDetail={showDetail} />
    )}
  </section>
);

// The /integrations surface: the per-account integration operation queue +
// 30-day history. One list poll feeds the whole page (principle 31). The page
// makes the per-account serialization visible: an op QUEUES behind an in-flight
// op on the same Google account so the two never contend the rate-limit bucket.
export const IntegrationsRoute = () => {
  useDocumentTitle("Integrations");
  const list = useIntegrationsList();

  const header = (
    <PageHeader
      title="Integrations"
      description="Gmail and Drive operations across all projects. Each Google account runs one Gmail and one Drive operation at a time; the rest queue so they never hit Google's rate limits. Queued operations can be cancelled."
    />
  );

  if (list.isLoading && !list.data) {
    return (
      <>
        {header}
        <div className={styles.page}>Loading integration operations…</div>
      </>
    );
  }
  if (list.isError) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.loadError}>
            Could not load integration operations.{" "}
            {list.error instanceof Error ? list.error.message : ""}
          </div>
        </div>
      </>
    );
  }

  const data = list.data ?? { queued: [], running: [], completed: [] };

  return (
    <>
      {header}
      <div className={styles.page}>
        {/* The confidentiality record, above the operations: it is about the
            operator's own mail, and it is the only place a withheld thread can be
            released. Renders nothing for somebody with no connected inbox. */}
        <MailboxReadLogSection />

        <Section
          title="Running"
          lede="In flight now - up to one Gmail + one Drive per Google account."
          ops={data.running}
        />
        <Section
          title="Queued"
          lede="Waiting for an earlier operation on the same Google account and service (Gmail or Drive) to finish."
          ops={data.queued}
          showCancel
        />
        <Section
          title="Completed"
          lede="Finished in the last 30 days (completed, failed, or cancelled). A completed sync pulls files; those files are then extracted on separate workers - track extraction on each project's Files tab."
          ops={data.completed}
          showDetail
        />
      </div>
    </>
  );
};
